import {
  applyPhaseHistoryPatch,
  buildPhaseRecord,
  overlappingPhase,
  type PhaseHistoryPatch,
  type PhaseInput,
  type PhaseRecord,
  phaseSpan,
} from '~/models/phase'
import { type CalendarDate, toIsoTimestamp, unwrapDate } from '~/types/dates'
import type { PhaseId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

export async function getPhase(
  db: SolaDatabase,
  id: PhaseId,
): Promise<Result<PhaseRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.phases, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The single active phase, if any.
 *
 * Version 1 has at most one. The index is queried rather than scanning, and the
 * most recent start date wins if data ever contains more than one — a defensive
 * choice, not an invitation to create them.
 *
 * **A phase may now start in the future (D-107), so this can return a phase that
 * has not begun.** That is correct here and is handled by the callers, which ask
 * `projectHasStarted` before acting; the sort is left alone for the reason
 * `getActiveProject` documents. `createPhase` still runs the no-overlap check,
 * so a future-dated phase cannot be added beside a running one.
 */
export async function getActivePhase(
  db: SolaDatabase,
): Promise<Result<PhaseRecord | undefined, StorageError>> {
  try {
    const active = await db.getAllFromIndex(STORE.phases, 'status', 'active')
    const sorted = [...active].sort((left, right) => right.startDate.localeCompare(left.startDate))
    return ok(sorted[0])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function listPhases(
  db: SolaDatabase,
): Promise<Result<readonly PhaseRecord[], StorageError>> {
  try {
    return ok(await db.getAllFromIndex(STORE.phases, 'startDate'))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function createPhase(
  db: SolaDatabase,
  input: PhaseInput,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  const built = buildPhaseRecord(input, toIsoTimestamp(now))
  if (!built.ok) {
    return err(
      storageError(
        'invalidRecord',
        built.error.map((entry) => entry.message).join(' '),
        built.error,
      ),
    )
  }

  try {
    /* The same no-overlap constraint the edit and correction paths enforce
       (D-070). Creation went without it until D-103 §3 because its only caller
       was project setup, which runs against an empty store — the next-phase flow
       is the first time a phase is appended to a history that already has one,
       and a handover starting on the day the previous phase ended is exactly the
       mistake a date field invites. */
    const all = await db.getAll(STORE.phases)
    const clash = overlappingPhase(built.value, all)
    if (clash !== undefined) {
      const span = phaseSpan(clash)
      return err(
        storageError(
          'invalidRecord',
          `Those dates overlap the phase running ${span.from} to ${span.to}. Phases may not overlap.`,
        ),
      )
    }

    await db.put(STORE.phases, built.value)
    notifyWritten(STORE.phases)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Edit the active phase in place.
 *
 * **The refusal below is narrowed by D-070, not removed.** It once meant
 * "editing past phases is not available yet" (L-003, now closed). It now means
 * something permanent: this path edits the target range, which is **frozen** on
 * a completed phase because changing it would silently rewrite whether
 * historical weeks were on track. Corrections to a past phase go through
 * `correctPhaseHistory`, which cannot express a range change at all.
 *
 * Changing the target weekly range changes what the rules engine concludes from
 * the same numbers, which is why this is a deliberate user action reached from
 * Settings and never something the app does on its own. Finalized reviews are
 * unaffected regardless: each carries an immutable `phaseSnapshot` (D-046).
 *
 * Moving the planned end date clears `plannedEndAcknowledged` — an
 * acknowledgement of the old date says nothing about the new one, and leaving it
 * set would suppress the D-020 warning for an end the user has never seen.
 */
export async function updateActivePhase(
  db: SolaDatabase,
  id: PhaseId,
  input: PhaseInput,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.phases, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That phase no longer exists.'))
    }
    if (existing.status !== 'active') {
      return err(
        storageError(
          'invalidRecord',
          'Only the active phase can be edited here. A completed phase keeps its target range and type; correct its dates, notes or transition reason instead.',
        ),
      )
    }

    const built = buildPhaseRecord(input, toIsoTimestamp(now), {
      id: existing.id,
      status: existing.status,
      createdAt: existing.createdAt,
      ...(existing.transitionReason === undefined
        ? {}
        : { transitionReason: existing.transitionReason }),
      ...(existing.corrections === undefined ? {} : { corrections: existing.corrections }),
    })
    if (!built.ok) {
      return err(
        storageError(
          'invalidRecord',
          built.error.map((entry) => entry.message).join(' '),
          built.error,
        ),
      )
    }

    const endDateMoved = built.value.plannedEndDate !== existing.plannedEndDate
    const record: PhaseRecord = {
      ...built.value,
      ...(existing.plannedEndAcknowledged === true && !endDateMoved
        ? { plannedEndAcknowledged: true }
        : {}),
    }

    /* The no-overlap constraint is a property of the history, not of one screen
       (D-070). Enforcing it only on the correction path would leave this one
       free to move the active phase's start back into a completed phase. */
    const all = await db.getAll(STORE.phases)
    const clash = overlappingPhase(record, all)
    if (clash !== undefined) {
      const span = phaseSpan(clash)
      return err(
        storageError(
          'invalidRecord',
          `Those dates overlap the phase running ${span.from} to ${span.to}. Phases may not overlap.`,
        ),
      )
    }

    await db.put(STORE.phases, record)
    notifyWritten(STORE.phases)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Close a phase.
 *
 * Never called automatically. A transition always requires an explicit user
 * decision (D-020); this is the persistence step of that decision, not a
 * trigger for it.
 */
export async function completePhase(
  db: SolaDatabase,
  id: PhaseId,
  actualEndDate: CalendarDate,
  now: Date,
  transitionReason?: string,
): Promise<Result<PhaseRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.phases, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That phase no longer exists.'))
    }
    /* Closing an already-closed phase would move its end date with no record of
       the change, which is the one thing D-070 does not allow. A correction is
       the route to a different end date, and it keeps the original. */
    if (existing.status === 'completed') {
      return err(
        storageError(
          'invalidRecord',
          'That phase is already complete. Correct its end date instead of ending it again.',
        ),
      )
    }
    if (unwrapDate(actualEndDate) < unwrapDate(existing.startDate)) {
      return err(storageError('invalidRecord', 'A phase cannot end before the day it started.'))
    }

    const record: PhaseRecord = {
      ...existing,
      status: 'completed',
      actualEndDate,
      ...(transitionReason === undefined || transitionReason === '' ? {} : { transitionReason }),
      updatedAt: toIsoTimestamp(now),
    }

    await db.put(STORE.phases, record)
    notifyWritten(STORE.phases)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Correct a phase's history (D-070), closing L-003.
 *
 * Works on **any** phase, active or completed, because the constraint that
 * matters is what may change rather than which record it belongs to: the patch
 * type carries no type, range or status, so none of them can move through here.
 * That is what makes "an edit may not reopen a completed phase" structural.
 *
 * Every changed field records what it replaced. Nothing is deletable — this
 * function has no delete, and there is deliberately no `deletePhase` anywhere in
 * this repository.
 */
export async function correctPhaseHistory(
  db: SolaDatabase,
  id: PhaseId,
  patch: PhaseHistoryPatch,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.phases, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That phase no longer exists.'))
    }

    const others = (await db.getAll(STORE.phases)).filter((phase) => phase.id !== id)
    const corrected = applyPhaseHistoryPatch(existing, patch, toIsoTimestamp(now), others)
    if (!corrected.ok) {
      return err(
        storageError(
          'invalidRecord',
          corrected.error.map((entry) => entry.message).join(' '),
          corrected.error,
        ),
      )
    }

    await db.put(STORE.phases, corrected.value)
    notifyWritten(STORE.phases)
    return ok(corrected.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Record that the user has seen the planned-end warning (D-020).
 *
 * **This changes nothing else, and that is the point.** Acknowledging is not
 * deciding: the phase keeps its dates, its targets and its status, and the app
 * has still not transitioned anything. It only stops the warning shouting on
 * every screen while the user thinks about it.
 */
export async function acknowledgePlannedEnd(
  db: SolaDatabase,
  id: PhaseId,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.phases, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That phase no longer exists.'))
    }

    const record: PhaseRecord = {
      ...existing,
      plannedEndAcknowledged: true,
      updatedAt: toIsoTimestamp(now),
    }

    await db.put(STORE.phases, record)
    notifyWritten(STORE.phases)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
